import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TreeDeciduous } from 'lucide-react';
import chineseLanternIcon from '@assets/images/chinese-lantern-.png';
import { ROUTES } from '@app/router/routes';
import { FEATURE_FLAGS, isFeatureAccessible } from '@shared/config/featureFlags';
import CloseButton from '@shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

const renderNavIcon = (icon, className) => {
  if (typeof icon === 'string') {
    return <span className={`material-symbols-outlined text-2xl ${className}`}>{icon}</span>;
  }
  if (typeof icon === 'object' && icon?.type === 'image') {
    // 用 mask-image 把 PNG 當「透明遮罩」來上色，讓它像文字 icon 一樣吃 currentColor。
    // (前提：PNG 需有透明度/遮罩輪廓；多色圖片不會保留原本色彩。)
    return (
      <span
        className={`size-6 shrink-0 inline-block ${className}`}
        style={{
          WebkitMaskImage: `url(${icon.src})`,
          maskImage: `url(${icon.src})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
        aria-hidden
      />
    );
  }
  const Icon = icon;
  return <Icon className={`size-6 shrink-0 ${className}`} aria-hidden />;
};

/**
 * 導航選單組件 - Burger Menu 展開的側邊欄
 */
const NavigationMenu = ({ isOpen, onClose, user = null }) => {
  const isLoggedIn = !!user;
  const { t } = useLanguage();

  const interactiveItems = [
    ...(isFeatureAccessible(FEATURE_FLAGS.DNA, isLoggedIn) ? [{
      titleKey: 'nav.dna',
      icon: 'psychology',
      path: ROUTES.DNA,
    }] : []),
    {
      titleKey: 'nav.lampBlessing',
      icon: { type: 'image', src: chineseLanternIcon },
      path: ROUTES.LAMP_BULLETIN,
    },
    ...(isFeatureAccessible(FEATURE_FLAGS.QUIZ, isLoggedIn) ? [{
      titleKey: 'nav.quiz',
      icon: 'quiz',
      path: ROUTES.QUIZ,
    }] : []),
    ...(isFeatureAccessible(FEATURE_FLAGS.POSTCARD, isLoggedIn) ? [{
      titleKey: 'nav.postcard',
      icon: 'mail',
      path: ROUTES.POSTCARD_CREATE,
    }] : []),
    ...(isFeatureAccessible(FEATURE_FLAGS.LIVE_COMMENT, isLoggedIn) ? [{
      titleKey: 'nav.liveComment',
      icon: 'record_voice_over',
      path: ROUTES.LIVE_COMMENT,
    }] : []),
    ...(isFeatureAccessible(FEATURE_FLAGS.JUE_GUANG, isLoggedIn) ? [{
      titleKey: 'nav.jueGuang',
      icon: 'photo_library',
      path: ROUTES.JUE_GUANG,
    }] : []),
    ...(isFeatureAccessible(FEATURE_FLAGS.WISH_TREE, isLoggedIn) ? [{
      titleKey: 'nav.wishTree',
      icon: TreeDeciduous,
      path: ROUTES.WISH_TREE,
    }] : []),
  ];

  return (
    <>
      {/* 背景遮罩 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity cursor-pointer"
          onClick={onClose}
        />
      )}

      {/* 側邊選單 */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-xl font-bold text-primary">{t('header.menuTitle')}</h2>
          <CloseButton btnColor="text-primary" onClick={onClose} />
        </div>

        {/* 導航項目 */}
        <nav className="flex-1 min-h-0 overflow-y-auto p-4">
          {/* 文星點燈 */}
          <Link
            to={ROUTES.WISDOM_LAMP}
            onClick={onClose}
            className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-[--color-buddha-beige] transition-colors group cursor-pointer"
          >
            <span className="material-symbols-outlined text-2xl text-primary group-hover:text-gold">candle</span>
            <span className="text-base font-medium text-[--color-text-dark] group-hover:text-gold">{t('nav.wisdomLamp')}</span>
          </Link>

          {/* 中台心傳 */}
          {isFeatureAccessible(FEATURE_FLAGS.TRANSMISSION, isLoggedIn) && (
            <Link
              to={ROUTES.TRANSMISSION}
              onClick={onClose}
              className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-[--color-buddha-beige] transition-colors group cursor-pointer"
            >
              <span className="material-symbols-outlined text-2xl text-primary group-hover:text-gold">temple_buddhist</span>
              <span className="text-base font-medium text-[--color-text-dark] group-hover:text-gold">{t('nav.transmission')}</span>
            </Link>
          )}

          {/* 護博會 */}
          {isFeatureAccessible(FEATURE_FLAGS.GUARDIAN_EXPO, isLoggedIn) && (
            <Link
              to={ROUTES.GUARDIAN_EXPO}
              onClick={onClose}
              className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-[--color-buddha-beige] transition-colors group cursor-pointer"
            >
              <span className="material-symbols-outlined text-2xl text-primary group-hover:text-gold">groups</span>
              <span className="text-base font-medium text-[--color-text-dark] group-hover:text-gold">{t('nav.guardianExpo')}</span>
            </Link>
          )}

          {/* 分隔線 */}
          <div className="border-t border-stone-200 my-2" />

          {/* 互動專區 */}
          <p className="px-4 py-1 text-xs font-medium text-stone-400 tracking-widest">{t('nav.interactiveSection')}</p>

          {interactiveItems.map((item) =>
            item.disabled ? (
              <div
                key={item.path}
                className="flex items-center gap-4 px-4 py-4 rounded-lg opacity-40 cursor-not-allowed"
              >
                {renderNavIcon(item.icon, 'text-primary')}
                <span className="text-base font-medium text-[--color-text-dark]">{t(item.titleKey)}</span>
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-[--color-buddha-beige] transition-colors group cursor-pointer"
              >
                {renderNavIcon(item.icon, 'text-primary group-hover:text-gold')}
                <span className="text-base font-medium text-[--color-text-dark] group-hover:text-gold">{t(item.titleKey)}</span>
              </Link>
            )
          )}
        </nav>

        {/* Footer */}
        <div className="shrink-0 p-6 border-t border-stone-200">
          <p className="text-sm text-center text-stone-500">
            {t('nav.anniversary')}
          </p>
        </div>
      </div>
    </>
  );
};

NavigationMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default NavigationMenu;
